/**
 * createDragImage
 *
 * Build a custom HTML5 drag-image chip for token / expression-template
 * drags out of the input rail. The chip is appended to `document.body`,
 * attached to the drag via `setDragImage`, and cleaned up on the next tick
 * (the browser has already snapshotted the image by then).
 *
 * Why not Fluent tokens for the chip styling directly? `document.body` sits
 * *outside* the FluentProvider scope, so `var(--colorBrandBackground)` and
 * friends do not resolve and the browser falls back to defaults (transparent
 * bg, serif font). Callers therefore pass *resolved* values: either a
 * literal hex / rgb string, or a Fluent token resolved against an element
 * inside the provider via {@link resolveTokenColor}.
 *
 * The drag-image lifetime is owned by this helper: it appends, calls
 * `setDragImage`, and schedules removal on the next tick. Callers don't
 * have to track or clean up the element.
 */

export interface DragImageOptions {
  /** Visible chip label (token title or function template). */
  readonly text: string;
  /**
   * Resolved background color. Hex / rgb literal — *not* a Fluent CSS
   * variable, since the chip lives outside the provider scope. Callers
   * can resolve a Fluent token via {@link resolveTokenColor}.
   */
  readonly backgroundColor: string;
  /**
   * Resolved foreground color. Same constraint as `backgroundColor`:
   * must be a literal value, not a Fluent CSS variable, because the chip
   * is appended to `<body>` outside the FluentProvider scope. Defaults to
   * white when omitted so a missing value never renders an invisible
   * label on a colored chip.
   */
  readonly color?: string;
  /**
   * Resolved font-family. Typically read from
   * `getComputedStyle(sourceRow).fontFamily` so the chip inherits the row's
   * font (system sans for tokens, monospace for function templates).
   */
  readonly fontFamily: string;
  /**
   * When true, adds `white-space: nowrap` so a multi-word template like
   * `concat('a', 'b')` renders on one line instead of wrapping.
   */
  readonly nowrap?: boolean;
}

/**
 * Attach a styled drag-image chip to a `dragstart` event. Returns nothing —
 * the helper owns the DOM node lifetime.
 */
export function createDragImage(
  dataTransfer: DataTransfer,
  options: DragImageOptions,
): void {
  const dragImage = document.createElement('div');
  // Default to white text when the caller omits `color` so the chip can
  // never render with invisible text on a colored background. Callers
  // that want themed text resolve `tokens.colorNeutralForegroundOnBrand`
  // (or similar) and pass it in explicitly.
  const textColor = options.color || 'white';
  dragImage.style.cssText = `
    position: absolute;
    top: -1000px;
    padding: 6px 12px;
    background: ${options.backgroundColor};
    color: ${textColor};
    border-radius: 4px;
    font-family: ${options.fontFamily};
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    ${options.nowrap ? 'white-space: nowrap;' : ''}
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  `;
  dragImage.textContent = options.text;
  document.body.appendChild(dragImage);
  dataTransfer.setDragImage(dragImage, 0, 0);
  // Remove on the next tick — the browser has already taken the snapshot.
  // Use `parentNode?.removeChild` so a rare double-cleanup (e.g., HMR
  // re-render mid-drag) doesn't throw.
  setTimeout(() => {
    if (dragImage.parentNode) dragImage.parentNode.removeChild(dragImage);
  }, 0);
}

/**
 * Matches the Fluent token shape `var(--name)` or `var(--name, fallback)`
 * and captures just the bare CSS variable name. Hoisted to module scope so
 * the regex isn't recompiled on every {@link resolveTokenColor} call.
 */
const TOKEN_VAR_PATTERN = /var\((--[^,)]+)/;

/**
 * Resolve a Fluent token CSS variable string (e.g.
 * `'var(--colorBrandBackground)'`) to its computed color against an element
 * inside the FluentProvider scope. Used by drag-image builders so the chip
 * — which lives in `<body>` outside the provider — can still use design
 * tokens instead of hardcoded hex values.
 *
 * Returns an empty string when the variable doesn't resolve (e.g., element
 * detached, theme not loaded yet, jsdom in tests). Callers chain a `||
 * '#hex'` fallback so the chip never ends up transparent.
 */
export function resolveTokenColor(
  scopeEl: Element,
  tokenValue: string,
): string {
  const match = TOKEN_VAR_PATTERN.exec(tokenValue);
  if (!match) return tokenValue.trim();
  return getComputedStyle(scopeEl).getPropertyValue(match[1]!).trim();
}
